import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  MapPin, 
  Clock, 
  Send,
  CheckCircle,
  Phone
} from 'lucide-react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    systemSize: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    setIsSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      city: '',
      systemSize: '',
      message: ''
    });
    
    // Reset success message after 5 seconds
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const contactInfo = [
    {
      icon: <Phone className="h-8 w-8 text-yellow-500" />,
      title: "Phone",
      details: ["+91 98493 68733", "+91 89190 28413"],
      action: "tel:+919849368733"
    },
    {
      icon: <MapPin className="h-8 w-8 text-yellow-500" />,
      title: "Address",
      details: ["Flat No.FT2, B-Block", "Sri sai Parimala Appartments", "Nagamalli Thota Junction", "Kakinada-533003"],
      action: "#"
    },
    {
      icon: <MapPin className="h-8 w-8 text-yellow-500" />,
      title: "Service Areas",
      details: ["Rajahmundry", "Kakinada", "Amalapuram", "Visakhapatnam", "Vizag", "Eluru", "Bhimavaram", "Tanuku", "Tuni", "Peddapuram"],
      action: "#"
    },
    {
      icon: <Clock className="h-8 w-8 text-yellow-500" />,
      title: "Working Hours",
      details: ["Mon-Sat: 9.30A.M - 6.30P.M", "Sun: closed"],
      action: "#"
    }
  ];

  const cities = [
    'Rajahmundry', 'Kakinada', 'Amalapuram', 
    'Visakhapatnam', 'Vizag', 'Eluru', 'Bhimavaram',
    'Tanuku', 'Palakollu', 'Tadepalligudem', 'Nidadavole',
    'Tuni', 'Peddapuram', 'Pithapuram',
    'Mandapeta', 'Razole', 'Ramachandrapuram',
    'Mummidivaram', 'Kothapeta', 'Samalkot',
    'Yeleswaram', 'Gollaprolu', 'Tallarevu',
    'Uppalaguptam', 'Malikipuram', 'Allavaram',
    'Katrenikona', 'I. Polavaram', 'Devipatnam',
    'Maredumilli', 'Y. Ramavaram', 'Addateegala',
    'Rajanagaram', 'Anaparthy', 'Kadiam',
    'Sankhavaram', 'Kotananduru', 'Alamuru',
    'Atreyapuram', 'Ravulapalem', 'Kapileswarapuram',
    'Karapa', 'Thondangi', 'Sakhinetipalli',
    'Ainavilli', 'Ambajipeta'
  ];

  const systemSizes = [
    '1kW - 3kW (Residential)',
    '3kW - 10kW (Large Home)',
    '10kW - 50kW (Commercial)',
    '50kW - 100kW (Small Industry)',
    '100kW+ (Large Industry)'
  ];

  return (
    <>
      <Helmet>
        <title>Contact AE Solar - Get Free Solar Quote in East Godavari, West Godavari & Visakhapatnam | Call +91 98493 68733</title>
        <meta name="description" content="Contact AE Solar for free solar consultation in East Godavari, West Godavari districts and Visakhapatnam (Vizag). Call +91 98493 68733 or +91 89190 28413. Visit us at Kakinada office. Get free quote for solar panel installation with PM Solar Scheme subsidies." />
        <meta name="keywords" content="contact AE Solar, solar consultation East Godavari, solar consultation West Godavari, solar consultation Visakhapatnam, solar quote Kakinada, solar quote Eluru, solar quote Vizag, solar installation contact, free solar consultation, solar expert contact" />
        <link rel="canonical" href="https://aesolarsystems.in/contact" />
        <meta property="og:url" content="https://aesolarsystems.in/contact" />
        <meta property="og:title" content="Contact AE Solar - Get Free Solar Quote" />
        <meta property="og:description" content="Contact AE Solar for free solar consultation in East Godavari, West Godavari districts and Visakhapatnam (Vizag). Call +91 98493 68733. Get free quote for solar panel installation." />
        <meta property="og:image" content="https://aesolarsystems.in/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact AE Solar" />
        <meta name="twitter:description" content="Get free solar consultation and quote. Call +91 98493 68733 or visit our Kakinada office." />
      </Helmet>
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url('https://img.freepik.com/premium-photo/blue-solar-photo-voltaic-panels-system-producing-renewable-clean-energy-rural-landscape-setting-sun_127089-3464.jpg?semt=ais_hybrid&w=740&q=80')`
        }}></div>
        
        {/* Dark Overlay for Better Text Visibility */}
        <div className="absolute inset-0 bg-blue-900/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl lg:text-5xl font-bold mb-6"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white max-w-3xl mx-auto font-medium"
          >
            Get in touch with our solar experts for a free consultation and quote. 
            We're here to help you go solar!
          </motion.p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <div className="mb-4 flex justify-center">{info.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{info.title}</h3>
                <div className="space-y-2">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600 text-sm">{detail}</p>
                  ))}
                </div>
                {info.action !== "#" && (
                  <a
                    href={info.action}
                    className="inline-block mt-4 text-yellow-600 hover:text-yellow-700 font-medium"
                  >
                    Contact Now
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Get Your Free Solar Quote
              </h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and our solar experts will get back to you within 24 hours 
                with a customized quote and consultation.
              </p>

              {isSubmitted && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Thank you! We'll contact you soon with your solar quote.
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <select
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    >
                      <option value="">Select your city</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="systemSize" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred System Size
                  </label>
                  <select
                    id="systemSize"
                    name="systemSize"
                    value={formData.systemSize}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    <option value="">Select system size</option>
                    {systemSizes.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Requirements
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="Tell us about your specific requirements..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 py-4 px-6 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Get Free Quote
                </button>
              </form>
            </motion.div>

            {/* Map & Quick Contact */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-gray-100 p-8 rounded-lg h-full">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Quick Contact
                </h3>
                
                <div className="space-y-6 mb-8">
                  <div className="flex items-start">
                    <Phone className="h-6 w-6 text-yellow-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Call Us</h4>
                      <p className="text-gray-600">Speak directly with our experts</p>
                      <a
                        href="tel:+919849368733"
                        className="text-yellow-600 hover:text-yellow-700 font-medium block mt-1"
                      >
                        +91 98493 68733
                      </a>
                      <a
                        href="tel:+918919028413"
                        className="text-yellow-600 hover:text-yellow-700 font-medium block mt-1"
                      >
                        +91 89190 28413
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="h-6 w-6 text-yellow-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Visit Us</h4>
                      <p className="text-gray-600">Our office location</p>
                      <p className="text-gray-700 text-sm mt-1">
                        Flat No.FT2, B-Block,<br />
                        Sri sai Parimala Appartments,<br />
                        Nagamalli Thota Junction,<br />
                        Kakinada-533003
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Why Choose Us?</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Free site survey and consultation</li>
                    <li>• Customized solar solutions</li>
                    <li>• Government subsidy assistance</li>
                    <li>• 25+ year warranty on panels</li>
                    <li>• Professional installation team</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Areas Map */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Service Areas in East Godavari, West Godavari Districts & Visakhapatnam, Andhra Pradesh
            </h2>
            <p className="text-xl text-gray-300">
              We provide solar installation services across these major cities
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6">
            {cities.map((city, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-800 p-4 rounded-lg text-center hover:bg-yellow-600 transition-colors cursor-pointer"
              >
                <MapPin className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
                <p className="font-medium">{city}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Ready to Start Your Solar Journey?
          </h2>
          <p className="text-xl text-gray-800 mb-8 max-w-2xl mx-auto">
            Don't wait! Contact us today for a free consultation and start saving on your electricity bills
          </p>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contact;
